import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  BookOpen,
  Boxes,
  FolderTree,
  LayoutGrid,
  Link2,
  LogOut,
  Mail,
  Pencil,
  Plus,
  Search,
  Shield,
  Trash2,
  Upload,
  Users,
  ImagePlus,
  Check,
  Download,
  Package,
  Settings,
  GripVertical,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { PublicMenuSettings } from "@/components/PublicMenuSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (path) => (path ? `${BACKEND_URL}/api/files/${path}` : null);
const menuCoverUrl = (value) => value ? /^https?:\/\//i.test(value) ? value : imgUrl(value) : "";
const fmtPrice = (value) => {
  const price = String(value ?? "").trim();
  if (!price) return "—";
  if (/^\d+(\.\d+)?$/.test(price)) return `$${Number(price).toLocaleString("es-CO")}`;
  if (/^[\d.,]+$/.test(price)) return `$${price}`;
  return price;
};

const EXAMPLE_CATEGORY_IMAGE = "https://images.unsplash.com/photo-1755608775883-ee296e9f7a3d?w=1200&q=80&auto=format&fit=crop";
const initialCategoryForm = { name: "", description: "", sort_order: "", image_url: "", image_path: "", card_image_url: EXAMPLE_CATEGORY_IMAGE, card_image_path: "", menu_image_url: EXAMPLE_CATEGORY_IMAGE, menu_image_path: "" };
const initialSubcategoryForm = { category_id: "", name: "", description: "", sort_order: "" };
const initialProductForm = {
  unique_code: "",
  name: "",
  description: "",
  category_id: "",
  subcategory_id: "",
  subcategory_type: "",
  product_units: "",
  base_price: "",
  allergens: "",
  badges: "",
  image_url: "",
  image_path: "",
  is_active: true,
};
const initialMenuForm = {
  name: "",
  slug: "",
  description: "",
  cover_image: "",
  template_slug: "gourmet",
  is_active: true,
  seo_index: true,
  direct_menu: false,
  direct_menu_image: "",
};
const initialAssignForm = { category_id: "", subcategory_id: "", product_id: "", menu_price: "" };
const initialUserForm = { email: "", name: "" };
const defaultPublicMenuSettings = { logo_path: "", background_color: "#FDFBF7" };

function formatError(error, fallback = "Ocurrió un error") {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || JSON.stringify(item)).join(" ");
  return fallback;
}

function SidebarItem({ icon: Icon, label, active, onClick, testId }) {
  return (
    <div
      className={`sidebar-nav-item ${active ? "active" : ""}`}
      onClick={onClick}
      data-testid={testId}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="stat-card" data-testid={testId}>
      <div className="text-xs font-body uppercase tracking-wide" style={{ color: "#6B6A65" }}>{label}</div>
      <div className="mt-1 text-3xl font-heading font-bold" style={{ color: "#2A2A2A" }}>{value}</div>
    </div>
  );
}

function MenuAssignmentRow({ assignment, menuId, onRefresh }) {
  const [price, setPrice] = useState(assignment.price || assignment.base_price || 0);
  const handleSave = async () => {
    try {
      await api.admin.updateMenuProduct(menuId, assignment.assignment_id, { menu_price: Number(price) || 0 });
      toast.success("Precio del menú actualizado");
      onRefresh();
    } catch (error) {
      toast.error(formatError(error, "No se pudo actualizar el precio"));
    }
  };

  return (
    <div className="grid gap-3 rounded-2xl border border-[#E6E4DD] bg-[#F8F7F3] p-4 lg:grid-cols-[1fr_120px_140px_44px] lg:items-start" data-testid={`menu-assignment-${assignment.assignment_id}`}>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-lg font-heading font-semibold text-[#2A2A2A]" data-testid={`menu-assignment-title-${assignment.assignment_id}`}>{assignment.name}</p>
          {assignment.product_units && Number(assignment.product_units) > 1 && (
            <span className="rounded-full bg-[#e8f5ea] px-2 py-0.5 text-xs font-bold uppercase tracking-[0.14em] text-[#1d6a37]" data-testid={`menu-assignment-units-${assignment.assignment_id}`}>
              {assignment.product_units} uds
            </span>
          )}
        </div>
        {assignment.description && <p className="mt-2 text-sm leading-7 text-[#6B6A65]">{assignment.description}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          {(assignment.badges || []).map((badge) => (
            <span key={badge} className="rounded-full bg-[#e4f3e8] px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-[#1d6a37]" data-testid={`assignment-badge-${assignment.assignment_id}-${badge}`}>
              {badge}
            </span>
          ))}
          {(assignment.allergens || []).map((allergen) => (
            <span key={allergen} className="rounded-full bg-[#f6e4e9] px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-[#7d2138]" data-testid={`assignment-allergen-${assignment.assignment_id}-${allergen}`}>
              {allergen}
            </span>
          ))}
        </div>
      </div>

      <div className="text-sm text-[#6B6A65]" data-testid={`menu-assignment-base-price-${assignment.assignment_id}`}>
        <div className="text-xs uppercase tracking-[0.18em] text-[#A89F9A]">Base</div>
        <div className="mt-2 font-semibold text-[#2A2A2A]">{fmtPrice(assignment.base_price)}</div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`price-${assignment.assignment_id}`} className="text-xs uppercase tracking-[0.18em] text-[#A89F9A]">Precio menú</Label>
        <Input
          id={`price-${assignment.assignment_id}`}
          type="number"
          value={price}
          onChange={(event) => setPrice(event.target.value)}
          className="border-[#E6E4DD] bg-white"
          data-testid={`menu-assignment-price-input-${assignment.assignment_id}`}
        />
        <Button className="w-full bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={handleSave} data-testid={`menu-assignment-save-${assignment.assignment_id}`}>
          Guardar
        </Button>
      </div>

      <Button
        variant="outline"
        className="border-[#E6E4DD] bg-white"
        onClick={async () => {
          if (!window.confirm("¿Retirar producto del menú?")) return;
          try {
            await api.admin.deleteMenuProduct(menuId, assignment.assignment_id);
            toast.success("Producto retirado del menú");
            onRefresh();
          } catch (error) {
            toast.error(formatError(error, "No se pudo retirar"));
          }
        }}
        data-testid={`menu-assignment-remove-${assignment.assignment_id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("menus");
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [menus, setMenus] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(() => localStorage.getItem("activeMenuId") || null);
  const [selectedMenuStructure, setSelectedMenuStructure] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogCategoryId, setCatalogCategoryId] = useState("");
  const [catalogSubcategoryId, setCatalogSubcategoryId] = useState("");
  const [expandedCats, setExpandedCats] = useState({});
  const [expandedSubs, setExpandedSubs] = useState({});
  const [showLinkCatDialog, setShowLinkCatDialog] = useState(false);
  const [availableCats, setAvailableCats] = useState([]);

  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(initialCategoryForm);
  const [categoryCardPreviewError, setCategoryCardPreviewError] = useState(false);
  const [categoryMenuPreviewError, setCategoryMenuPreviewError] = useState(false);

  const [showSubcategoryDialog, setShowSubcategoryDialog] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [subcategoryForm, setSubcategoryForm] = useState(initialSubcategoryForm);

  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(initialProductForm);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuForm, setMenuForm] = useState(initialMenuForm);
  const [publicMenuSettings, setPublicMenuSettings] = useState(defaultPublicMenuSettings);
  const [savingPublicMenuSettings, setSavingPublicMenuSettings] = useState(false);

  const [assignForm, setAssignForm] = useState(initialAssignForm);

  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userForm, setUserForm] = useState(initialUserForm);
  const [users, setUsers] = useState([]);
  const [usersSearch, setUsersSearch] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const [usersPages, setUsersPages] = useState(1);

  const [smtpForm, setSmtpForm] = useState({ host: "", port: 587, username: "", password: "", security: "TLS", sender_email: "", sender_name: "MenuManager", enabled: false });
  const [smtpTesting, setSmtpTesting] = useState(false);

  const [productImportPreview, setProductImportPreview] = useState(null);
  const [menuImportPreview, setMenuImportPreview] = useState(null);
  const [productImporting, setProductImporting] = useState(false);
  const [menuImporting, setMenuImporting] = useState(false);
  const [showProductImportDialog, setShowProductImportDialog] = useState(false);
  const [showMenuImportDialog, setShowMenuImportDialog] = useState(false);

  const subcategoriesByCategory = useMemo(() => {
    return subcategories.reduce((acc, item) => {
      acc[item.category_id] = [...(acc[item.category_id] || []), item];
      return acc;
    }, {});
  }, [subcategories]);

  const productsBySubcategory = useMemo(() => {
    return products.reduce((acc, product) => {
      const items = acc[product.subcategory_id] || [];
      acc[product.subcategory_id] = [...items, product];
      return acc;
    }, {});
  }, [products]);

  const activeMenu = useMemo(() => menus.find((m) => m.id === selectedMenuId) || null, [menus, selectedMenuId]);
  const publicMenuUrl = activeMenu ? `${window.location.origin}/menu/${activeMenu.slug}` : "";

  const openLinkCatDialog = async () => {
    if (!selectedMenuId) return;
    try {
      const { data } = await api.admin.getAvailableCategories(selectedMenuId);
      setAvailableCats(data);
      setShowLinkCatDialog(true);
    } catch (error) {
      toast.error("No se pudieron cargar las categorías disponibles");
    }
  };

  const linkCategory = async (categoryId) => {
    try {
      await api.admin.linkCategoryToMenu(selectedMenuId, categoryId);
      toast.success("Categoría vinculada al menú");
      setAvailableCats((prev) => prev.filter((c) => c.id !== categoryId));
      await refreshEverything();
    } catch (error) {
      toast.error(formatError(error, "No se pudo vincular"));
    }
  };

  const unlinkCategory = async (categoryId) => {
    if (!window.confirm("¿Quitar esta categoría del menú? (no se elimina, solo se desvincula)")) return;
    try {
      await api.admin.unlinkCategoryFromMenu(selectedMenuId, categoryId);
      toast.success("Categoría desvinculada del menú");
      await refreshEverything();
    } catch (error) {
      toast.error(formatError(error, "No se pudo desvincular"));
    }
  };

  const changeTemplate = async (templateSlug) => {
    if (!selectedMenuId) return;
    try {
      await api.admin.updateMenuTemplate(selectedMenuId, templateSlug);
      toast.success("Plantilla actualizada");
      await refreshEverything();
    } catch (error) {
      toast.error(formatError(error, "No se pudo cambiar la plantilla"));
    }
  };

  const downloadQR = () => {
    const canvas = document.querySelector("#menu-qr-canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `qr-${activeMenu?.slug || "menu"}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const currentCatalogSubcategories = useMemo(
    () => subcategories.filter((item) => !catalogCategoryId || item.category_id === catalogCategoryId),
    [catalogCategoryId, subcategories]
  );

  const productDialogSubcategories = useMemo(
    () => subcategories.filter((item) => item.category_id === productForm.category_id),
    [productForm.category_id, subcategories]
  );

  const assignDialogSubcategories = useMemo(
    () => subcategories.filter((item) => item.category_id === assignForm.category_id),
    [assignForm.category_id, subcategories]
  );

  const assignableProducts = useMemo(() => {
    const assignedIds = new Set(
      (selectedMenuStructure?.categories || []).flatMap((category) =>
        category.subcategories.flatMap((subcategory) => subcategory.products.map((product) => product.id))
      )
    );
    return products.filter((item) => {
      if (assignedIds.has(item.id)) return false;
      if (assignForm.category_id && item.category_id !== assignForm.category_id) return false;
      if (assignForm.subcategory_id && item.subcategory_id !== assignForm.subcategory_id) return false;
      return true;
    });
  }, [assignForm.category_id, assignForm.subcategory_id, products, selectedMenuStructure]);

  const filteredProducts = useMemo(() => {
    return products.filter((item) => {
      const search = catalogSearch.trim().toLowerCase();
      const matchesSearch = !search || item.name.toLowerCase().includes(search) || item.unique_code.toLowerCase().includes(search);
      const matchesCategory = !catalogCategoryId || item.category_id === catalogCategoryId;
      const matchesSubcategory = !catalogSubcategoryId || item.subcategory_id === catalogSubcategoryId;
      return matchesSearch && matchesCategory && matchesSubcategory;
    });
  }, [catalogCategoryId, catalogSearch, catalogSubcategoryId, products]);

  const loadScoped = useCallback(async (menuId) => {
    const [catsRes, subsRes] = await Promise.all([
      api.admin.getCategories(menuId || undefined),
      api.admin.getSubcategories(undefined, menuId || undefined),
    ]);
    setCategories(catsRes.data);
    setSubcategories(subsRes.data);
  }, []);

  const loadCore = useCallback(async () => {
    try {
      const [statsRes, productsRes, menusRes, templatesRes, publicSettingsRes] = await Promise.all([
        api.admin.stats(),
        api.admin.getProducts(),
        api.admin.getMenus(),
        api.admin.getTemplates(),
        api.admin.getPublicMenuSettings(),
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setMenus(menusRes.data);
      setTemplates(templatesRes.data);
      setPublicMenuSettings({ ...defaultPublicMenuSettings, ...publicSettingsRes.data });
      const activeId = selectedMenuId || (menusRes.data.length > 0 ? menusRes.data[0].id : null);
      if (activeId && activeId !== selectedMenuId) setSelectedMenuId(activeId);
      await loadScoped(activeId);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo cargar el panel");
    }
  }, [selectedMenuId, loadScoped]);

  const loadMenuStructure = useCallback(async (menuId) => {
    if (!menuId) return setSelectedMenuStructure(null);
    try {
      const { data } = await api.admin.getMenuStructure(menuId);
      setSelectedMenuStructure(data);
    } catch (error) {
      toast.error("No se pudo cargar la estructura del menú");
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.admin.getUsers({ search: usersSearch, page: usersPage, limit: 10 });
      setUsers(data.users);
      setUsersPages(data.pages);
    } catch (error) {
      toast.error("No se pudo cargar usuarios");
    }
  }, [usersPage, usersSearch]);

  const loadSmtp = useCallback(async () => {
    try {
      const { data } = await api.admin.getSmtp();
      setSmtpForm(data);
    } catch (error) {
      toast.error("No se pudo cargar SMTP");
    }
  }, []);

  useEffect(() => {
    api.auth.me()
      .then((response) => {
        if (response.data.role !== "admin") throw new Error("No autorizado");
        setUser(response.data);
        loadCore();
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      });
  }, [loadCore, navigate]);

  useEffect(() => {
    if (selectedMenuId) {
      localStorage.setItem("activeMenuId", selectedMenuId);
      loadMenuStructure(selectedMenuId);
      loadScoped(selectedMenuId);
    }
  }, [loadMenuStructure, loadScoped, selectedMenuId]);

  useEffect(() => {
    if (tab === "users") loadUsers();
    if (tab === "smtp") loadSmtp();
  }, [loadSmtp, loadUsers, tab]);

  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } finally {
      navigate("/login");
    }
  };

  const handleImageUpload = async (event, onSet) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.upload(formData);
      await onSet(data.path);
      toast.success("Imagen subida");
    } catch (error) {
      toast.error(formatError(error, "No se pudo subir la imagen"));
    } finally {
      setUploadingImage(false);
      event.target.value = "";
    }
  };

  const refreshEverything = async () => {
    await loadCore();
    if (selectedMenuId) await loadMenuStructure(selectedMenuId);
  };

  const updateSelectedMenu = async (changes) => {
    const menu = selectedMenuStructure?.menu;
    if (!menu) return;
    await api.admin.updateMenu(menu.id, {
      name: menu.name,
      slug: menu.slug,
      description: menu.description || "",
      cover_image: changes.cover_image ?? menu.cover_image ?? "",
      template_slug: "gourmet",
      is_active: menu.is_active !== false,
      seo_index: menu.seo_index !== false,
      direct_menu: changes.direct_menu ?? Boolean(menu.direct_menu),
      direct_menu_image: changes.direct_menu_image ?? menu.direct_menu_image ?? "",
    });
    await refreshEverything();
  };

  const updateSelectedMenuCover = async (coverImage) => updateSelectedMenu({ cover_image: coverImage });
  const updateSelectedDirectMenuImage = async (imagePath) => updateSelectedMenu({ direct_menu_image: imagePath });

  const toggleCat = (id) => setExpandedCats((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleSubcategory = (id) => setExpandedSubs((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleReorder = async (result) => {
    const { type, source, destination } = result;
    if (!destination) return;
    if (type === "category") {
      if (source.index === destination.index) return;
      const items = Array.from(categories);
      const [moved] = items.splice(source.index, 1);
      items.splice(destination.index, 0, moved);
      setCategories(items);
      try {
        await api.admin.reorderCategories(items.map((c) => c.id));
      } catch (error) {
        toast.error("No se pudo reordenar categorías");
        loadCore();
      }
      return;
    }
    if (source.droppableId !== destination.droppableId) return;
    if (source.index === destination.index) return;
    if (type === "product") {
      const subcategoryId = source.droppableId.replace("products-", "");
      const current = Array.from(productsBySubcategory[subcategoryId] || []);
      const [moved] = current.splice(source.index, 1);
      current.splice(destination.index, 0, moved);
      const positionById = new Map(current.map((product, index) => [product.id, index]));
      setProducts((prev) => prev.map((product) => positionById.has(product.id) ? { ...product, sort_order: positionById.get(product.id) } : product));
      try {
        await api.admin.reorderProducts(subcategoryId, current.map((product) => product.id));
      } catch (error) {
        toast.error("No se pudo reordenar productos");
        loadCore();
      }
      return;
    }
    // subcategory reorder (within the same category only)
    const categoryId = source.droppableId.replace("subs-", "");
    const current = Array.from(subcategoriesByCategory[categoryId] || []);
    const [moved] = current.splice(source.index, 1);
    current.splice(destination.index, 0, moved);
    const others = subcategories.filter((s) => s.category_id !== categoryId);
    setSubcategories([...others, ...current]);
    try {
      await api.admin.reorderSubcategories(categoryId, current.map((s) => s.id));
    } catch (error) {
      toast.error("No se pudo reordenar subcategorías");
      loadCore();
    }
  };

  const downloadProductTemplate = () => {
    const header = "unique_code\tname\tdescription\tcategory\tsubcategory\tsubcategory_type\tproduct_units\tbase_price\tallergens\tbadges\timage_url\n";
    const rows = products.slice(0, 3).map((p) =>
      `${p.unique_code || ""}\t${p.name}\t${p.description || ""}\t${p.category_name || ""}\t${p.subcategory_name || ""}\t${p.subcategory_type || ""}\t${p.product_units || ""}\t${p.base_price}\t${(p.allergens || []).join(", ")}\t${(p.badges || []).join(", ")}\t${p.image_url || ""}`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/tab-separated-values" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "template_productos.xls";
    a.click();
  };

  const handleProductImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.admin.previewProductImport(formData);
      setProductImportPreview(data);
      setShowProductImportDialog(true);
    } catch (error) {
      toast.error(formatError(error, "No se pudo procesar el Excel"));
    }
    event.target.value = "";
  };

  const handleMenuImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.admin.previewMenuImport(formData);
      setMenuImportPreview(data);
      setShowMenuImportDialog(true);
      toast.success("Vista previa de menús lista");
    } catch (error) {
      toast.error(formatError(error, "No se pudo procesar el Excel"));
    }
    event.target.value = "";
  };

  const applyMenuImportNow = async () => {
    if (!menuImportPreview) return;
    setMenuImporting(true);
    try {
      const { data } = await api.admin.applyMenuImport({ menus: menuImportPreview.menus, items: menuImportPreview.items });
      toast.success(`Menús importados: ${data.created} creados, ${data.updated} actualizados`);
      setMenuImportPreview(null);
      setShowMenuImportDialog(false);
      await refreshEverything();
    } catch (error) {
      toast.error(formatError(error, "No se pudo aplicar la importación"));
    } finally {
      setMenuImporting(false);
    }
  };

  const applyProductImportNow = async () => {
    if (!productImportPreview) return;
    if (!selectedMenuId) {
      toast.error("Selecciona un menú activo antes de importar productos");
      return;
    }
    setProductImporting(true);
    try {
      const { data } = await api.admin.applyProductImport({ menu_id: selectedMenuId, items: productImportPreview.items });
      toast.success(`Importación completada: ${data.created} creados, ${data.updated} actualizados`);
      setProductImportPreview(null);
      setShowProductImportDialog(false);
      await refreshEverything();
    } catch (error) {
      toast.error(formatError(error, "No se pudo aplicar la importación"));
    } finally {
      setProductImporting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="dashboard-layout" data-testid="admin-dashboard">
      <aside className="dashboard-sidebar">
        <div className="mb-8 flex items-center gap-3 px-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: "#C05C46" }}>
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-sm" style={{ color: "#2A2A2A" }}>MenuManager</div>
            <div className="text-xs" style={{ color: "#6B6A65" }}>Admin</div>
          </div>
        </div>

        <nav className="flex-1">
          <SidebarItem icon={LayoutGrid} label="Menús" active={tab === "menus"} onClick={() => setTab("menus")} testId="nav-menus" />
          <SidebarItem icon={FolderTree} label="Categorías" active={tab === "structure"} onClick={() => setTab("structure")} testId="nav-structure" />
          <SidebarItem icon={Boxes} label="Productos" active={tab === "catalog"} onClick={() => setTab("catalog")} testId="nav-catalog" />

          <div className="border-t pt-3 mb-1 mt-3" style={{ borderColor: "#E6E4DD" }}>
            <div className="flex items-center gap-2 px-3 mb-2">
              <Settings className="h-3.5 w-3.5" style={{ color: "#A89F9A" }} />
              <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "#A89F9A" }}>Configuración</span>
            </div>
            <SidebarItem icon={Users} label="Usuarios" active={tab === "users"} onClick={() => setTab("users")} testId="nav-users" />
            <SidebarItem icon={Mail} label="Configuración SMTP" active={tab === "smtp"} onClick={() => setTab("smtp")} testId="nav-smtp" />
          </div>
        </nav>

        <div className="border-t pt-4 mt-2" style={{ borderColor: "#E6E4DD" }}>
          <div className="text-xs mb-2 px-2 truncate" style={{ color: "#6B6A65" }} data-testid="admin-email-display">{user.email}</div>
          <div className="sidebar-nav-item" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Cerrar Sesión
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold" style={{ color: "#2A2A2A" }} data-testid="dashboard-title">Panel de menús digitales</h1>
            <p className="mt-1 text-sm font-body" style={{ color: "#6B6A65" }} data-testid="dashboard-description">
              Gestiona categorías, subcategorías, productos y menús públicos Gourmet.
            </p>
          </div>
          <div className="flex items-center gap-2 rounded-lg border px-4 py-2" style={{ borderColor: activeMenu ? "#C05C46" : "#E6E4DD", background: activeMenu ? "#C05C4610" : "white" }} data-testid="active-menu-indicator">
            <span className="h-2 w-2 rounded-full" style={{ background: activeMenu ? "#1D6A37" : "#C7C0B8" }} />
            <div>
              <div className="text-[10px] uppercase tracking-wider" style={{ color: "#A89F9A" }}>Menú activo</div>
              <div className="text-sm font-heading font-bold" style={{ color: activeMenu ? "#C05C46" : "#6B6A65" }} data-testid="active-menu-name">{activeMenu ? activeMenu.name : "Ninguno — elige uno en Menús"}</div>
            </div>
          </div>
        </div>

          {stats && (
            <div className="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <StatCard label="Menús" value={stats.menus} testId="stats-menus" />
              <StatCard label="Categorías" value={stats.categories} testId="stats-categories" />
              <StatCard label="Subcategorías" value={stats.subcategories} testId="stats-subcategories" />
              <StatCard label="Productos" value={stats.products} testId="stats-products" />
              <StatCard label="Admins" value={stats.users} testId="stats-users" />
            </div>
          )}

          {tab === "menus" && (
            <div className="animate-fade-in" data-testid="menus-tab">
              <PublicMenuSettings
                settings={publicMenuSettings}
                uploading={uploadingImage}
                saving={savingPublicMenuSettings}
                onChange={setPublicMenuSettings}
                onUpload={(event) => handleImageUpload(event, (path) => setPublicMenuSettings((previous) => ({ ...previous, logo_path: path })))}
                onSave={async () => {
                  setSavingPublicMenuSettings(true);
                  try {
                    const { data } = await api.admin.updatePublicMenuSettings(publicMenuSettings);
                    setPublicMenuSettings({ ...defaultPublicMenuSettings, ...data });
                    toast.success("Portada pública actualizada");
                  } catch (error) {
                    toast.error(formatError(error, "No se pudo guardar la portada pública"));
                  } finally {
                    setSavingPublicMenuSettings(false);
                  }
                }}
              />
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-2xl font-heading font-bold" style={{ color: "#2A2A2A" }}>Listado de menús</h2>
                <div className="flex flex-wrap gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium hover:bg-gray-50" style={{ borderColor: "#E6E4DD" }} data-testid="menu-import-label">
                    <Upload className="h-3 w-3" style={{ color: "#C05C46" }} /> Importar menús
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleMenuImportFile} data-testid="menu-import-input" />
                  </label>
                  <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={() => { setEditingMenu(null); setMenuForm(initialMenuForm); setShowMenuDialog(true); }} data-testid="add-menu-btn">
                    <Plus className="mr-2 h-4 w-4" /> Nuevo Menú
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-4 lg:flex-row" style={{ minHeight: "500px" }}>
                <div className="w-full flex-shrink-0 space-y-2 lg:w-72">
                  {menus.map((menu) => {
                    const publicUrl = `${window.location.origin}/menu/${menu.slug}`;
                    const active = selectedMenuId === menu.id;
                    return (
                      <div
                        key={menu.id}
                        onClick={() => setSelectedMenuId(menu.id)}
                        className="cursor-pointer rounded-lg border p-3 transition-all"
                        style={{ borderColor: active ? "#C05C46" : "#E6E4DD", background: active ? "#C05C4608" : "white" }}
                        data-testid={`menu-card-${menu.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-heading font-bold text-sm" style={{ color: active ? "#C05C46" : "#2A2A2A" }}>{menu.name}</div>
                          {menu.is_active
                            ? <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: "#E7F3EA", color: "#1D6A37" }}>Activo</span>
                            : <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: "#F5EBDF", color: "#8A6B49" }}>Borrador</span>}
                        </div>
                        <div className="text-[10px] font-mono" style={{ color: "#6B6A65" }}>/{menu.slug}</div>
                        <div className="mt-1 text-[10px]" style={{ color: "#A89F9A" }}>{menu.category_count} categorías · {menu.product_count} productos</div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          <button onClick={(e) => { e.stopPropagation(); setEditingMenu(menu); setMenuForm(menu); setShowMenuDialog(true); }} className="rounded border px-2 py-0.5 text-[10px] hover:bg-gray-50" style={{ borderColor: "#E6E4DD" }} data-testid={`edit-menu-${menu.id}`}>Editar</button>
                          <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(publicUrl).then(() => toast.success("Enlace copiado")); }} className="rounded border px-2 py-0.5 text-[10px] hover:bg-gray-50" style={{ borderColor: "#E6E4DD", color: "#C05C46" }} data-testid={`copy-menu-link-${menu.id}`}>Copiar enlace</button>
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm("¿Eliminar menú?")) return;
                            try {
                              await api.admin.deleteMenu(menu.id);
                              toast.success("Menú eliminado");
                              if (selectedMenuId === menu.id) setSelectedMenuId(null);
                              await loadCore();
                            } catch (error) {
                              toast.error(formatError(error, "No se pudo eliminar"));
                            }
                          }} className="rounded border px-2 py-0.5 text-[10px] text-red-500 hover:bg-red-50" style={{ borderColor: "#E6E4DD" }} data-testid={`delete-menu-${menu.id}`}>Eliminar</button>
                        </div>
                      </div>
                    );
                  })}
                  {menus.length === 0 && <p className="py-8 text-center text-sm" style={{ color: "#6B6A65" }}>Crea tu primer menú</p>}
                </div>

                <div className="min-w-0 flex-1 rounded-lg border border-[#E6E4DD] bg-white p-6" data-testid="menu-builder-panel">
                  {!selectedMenuStructure ? (
                    <div className="rounded-lg border border-dashed border-[#E6E4DD] px-6 py-16 text-center text-[#6B6A65]">
                      Selecciona un menú para administrar su estructura.
                    </div>
                  ) : (
                    <div className="space-y-6">
                        <div>
                          <p className="text-xs uppercase tracking-[0.22em] text-[#A89F9A]">Editor del menú</p>
                          <h2 className="mt-2 text-4xl font-heading" data-testid="selected-menu-name">{selectedMenuStructure.menu.name}</h2>
                          <p className="mt-2 text-sm leading-7 text-[#6B6A65]">/{selectedMenuStructure.menu.slug}</p>
                          {selectedMenuStructure.menu.description && <p className="mt-3 max-w-2xl text-sm leading-7 text-[#6B6A65]">{selectedMenuStructure.menu.description}</p>}

                          <div className="mt-5 max-w-xs space-y-2">
                            <Label className="text-xs uppercase tracking-wider" style={{ color: "#A89F9A" }}>Plantilla del menú</Label>
                            <select value={selectedMenuStructure.menu.template_slug || "gourmet"} onChange={(event) => changeTemplate(event.target.value)} className="h-11 w-full rounded-lg border border-[#E6E4DD] bg-white px-3 text-sm" data-testid="menu-template-select">
                              {templates.map((tpl) => <option key={tpl.slug} value={tpl.slug}>{tpl.name}</option>)}
                            </select>
                          </div>

                          <div className="mt-5 max-w-xl space-y-2">
                            <Label className="text-xs uppercase tracking-wider" style={{ color: "#A89F9A" }}>Enlace público</Label>
                            <div className="flex gap-2">
                              <Input readOnly value={publicMenuUrl} className="border-[#E6E4DD] bg-[#F8F7F3] text-sm" data-testid="menu-public-url" />
                              <Button variant="outline" className="border-[#E6E4DD] bg-white shrink-0" onClick={() => navigator.clipboard.writeText(publicMenuUrl).then(() => toast.success("Enlace copiado"))} data-testid="copy-public-url-btn">
                                <Link2 className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" className="border-[#E6E4DD] bg-white shrink-0" onClick={() => window.open(publicMenuUrl, "_blank")} data-testid="open-public-url-btn">
                                <BookOpen className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="mt-5 max-w-xl space-y-2" data-testid="menu-builder-cover-section">
                            <Label className="text-xs uppercase tracking-wider" style={{ color: "#A89F9A" }}>Imagen del listado público</Label>
                            <div className="flex flex-wrap items-center gap-3">
                              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[#E6E4DD] bg-[#F8F7F3] px-4 py-2 text-sm font-medium" data-testid="menu-builder-cover-upload-label">
                                <ImagePlus className="h-4 w-4" /> {uploadingImage ? "Subiendo..." : "Importar imagen"}
                                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => handleImageUpload(event, updateSelectedMenuCover)} data-testid="menu-builder-cover-upload-input" />
                              </label>
                              {selectedMenuStructure.menu.cover_image && <Button type="button" variant="outline" className="border-[#E6E4DD]" disabled={uploadingImage} onClick={async () => {
                                try {
                                  await updateSelectedMenuCover("");
                                  toast.success("Imagen del menú eliminada");
                                } catch (error) {
                                  toast.error(formatError(error, "No se pudo eliminar la imagen"));
                                }
                              }} data-testid="remove-menu-builder-cover-button"><Trash2 className="mr-2 h-4 w-4" /> Quitar</Button>}
                            </div>
                            {selectedMenuStructure.menu.cover_image && <div className="overflow-hidden rounded-lg border border-[#E6E4DD] bg-[#F8F7F3]" style={{ aspectRatio: "3 / 1" }} data-testid="menu-builder-cover-preview-container"><img src={menuCoverUrl(selectedMenuStructure.menu.cover_image)} alt="Vista previa de imagen del listado público" className="h-full w-full object-cover" data-testid="menu-builder-cover-preview" /></div>}
                          </div>

                          <div className="mt-5 flex max-w-xl items-center gap-3 rounded-lg bg-[#F8F7F3] px-4 py-4" data-testid="menu-direct-menu-setting">
                            <Switch checked={Boolean(selectedMenuStructure.menu.direct_menu)} onCheckedChange={async (checked) => {
                              try {
                                await updateSelectedMenu({ direct_menu: checked });
                                toast.success(checked ? "Carta directa activada" : "Vista por categorías activada");
                              } catch (error) {
                                toast.error(formatError(error, "No se pudo actualizar el modo de carta"));
                              }
                            }} data-testid="menu-direct-menu-switch" />
                            <div>
                              <p className="font-medium" style={{ color: "#2A2A2A" }} data-testid="menu-direct-menu-label">Carta directa</p>
                              <p className="text-sm" style={{ color: "#6B6A65" }} data-testid="menu-direct-menu-description">Muestra todos los productos en una sola carta, sin portada por categorías.</p>
                            </div>
                          </div>

                          {selectedMenuStructure.menu.direct_menu && <div className="mt-5 max-w-xl space-y-2" data-testid="menu-direct-hero-section">
                            <Label className="text-xs uppercase tracking-wider" style={{ color: "#A89F9A" }}>Imagen superior de Carta directa</Label>
                            <div className="flex flex-wrap items-center gap-3">
                              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[#E6E4DD] bg-[#F8F7F3] px-4 py-2 text-sm font-medium" data-testid="menu-direct-hero-upload-label">
                                <ImagePlus className="h-4 w-4" /> {uploadingImage ? "Subiendo..." : "Importar imagen"}
                                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => handleImageUpload(event, updateSelectedDirectMenuImage)} data-testid="menu-direct-hero-upload-input" />
                              </label>
                              {selectedMenuStructure.menu.direct_menu_image && <Button type="button" variant="outline" className="border-[#E6E4DD]" disabled={uploadingImage} onClick={async () => {
                                try {
                                  await updateSelectedDirectMenuImage("");
                                  toast.success("Imagen superior eliminada");
                                } catch (error) {
                                  toast.error(formatError(error, "No se pudo eliminar la imagen superior"));
                                }
                              }} data-testid="remove-menu-direct-hero-button"><Trash2 className="mr-2 h-4 w-4" /> Quitar</Button>}
                            </div>
                            {selectedMenuStructure.menu.direct_menu_image && <div className="overflow-hidden rounded-lg border border-[#E6E4DD] bg-[#F8F7F3]" style={{ aspectRatio: "2.5 / 1" }} data-testid="menu-direct-hero-preview-container"><img src={menuCoverUrl(selectedMenuStructure.menu.direct_menu_image)} alt="Vista previa de imagen superior de Carta directa" className="h-full w-full object-cover" data-testid="menu-direct-hero-preview" /></div>}
                          </div>}

                          <div className="mt-5 flex items-center gap-4">
                            <div className="rounded-lg border border-[#E6E4DD] bg-white p-3" data-testid="menu-qr-wrapper">
                              {publicMenuUrl && <QRCodeCanvas id="menu-qr-canvas" value={publicMenuUrl} size={120} level="M" includeMargin />}
                            </div>
                            <div>
                              <p className="text-sm font-medium" style={{ color: "#2A2A2A" }}>Código QR</p>
                              <p className="mt-1 text-xs" style={{ color: "#6B6A65" }}>Apunta al enlace público del menú.</p>
                              <Button className="mt-3 bg-[#C05C46] hover:bg-[#a94f3c] text-white" size="sm" onClick={downloadQR} data-testid="download-qr-btn">
                                <Download className="mr-2 h-4 w-4" /> Descargar QR
                              </Button>
                            </div>
                          </div>

                          <div className="mt-6 flex flex-wrap gap-2 text-xs">
                            <span className="rounded-full bg-[#F8F7F3] px-3 py-1">{selectedMenuStructure.counts.categories} categorías</span>
                            <span className="rounded-full bg-[#F8F7F3] px-3 py-1">{selectedMenuStructure.counts.subcategories} subcategorías</span>
                            <span className="rounded-full bg-[#F8F7F3] px-3 py-1">{selectedMenuStructure.counts.products} productos</span>
                          </div>
                        </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "structure" && (
            <div className="animate-fade-in" data-testid="structure-tab">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-heading font-bold" style={{ color: "#2A2A2A" }}>Categorías</h2>
                  <p className="mt-1 text-sm" style={{ color: "#6B6A65" }} data-testid="structure-active-menu">
                    {activeMenu ? <>Menú activo: <strong style={{ color: "#C05C46" }}>{activeMenu.name}</strong></> : "Selecciona un menú activo en la pestaña Menús"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={openLinkCatDialog} disabled={!activeMenu} className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium hover:bg-gray-50 disabled:opacity-40" style={{ borderColor: "#E6E4DD" }} data-testid="link-category-btn">
                    <Link2 className="h-3 w-3" style={{ color: "#C05C46" }} /> Vincular categoría
                  </button>
                  <button onClick={() => { setEditingSubcategory(null); setSubcategoryForm(initialSubcategoryForm); setShowSubcategoryDialog(true); }} disabled={!activeMenu} className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium hover:bg-gray-50 disabled:opacity-40" style={{ borderColor: "#E6E4DD" }} data-testid="add-subcategory-btn">
                    <Plus className="h-3 w-3" style={{ color: "#6B6A65" }} /> Nueva subcategoría
                  </button>
                  <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" disabled={!activeMenu} onClick={() => { setEditingCategory(null); setCategoryCardPreviewError(false); setCategoryMenuPreviewError(false); setCategoryForm(initialCategoryForm); setShowCategoryDialog(true); }} data-testid="add-category-btn">
                    <Plus className="mr-2 h-4 w-4" /> Nueva categoría
                  </Button>
                </div>
              </div>

              {!activeMenu ? (
                <div className="rounded-lg border border-dashed px-6 py-16 text-center text-sm" style={{ borderColor: "#E6E4DD", color: "#6B6A65" }} data-testid="structure-no-menu">
                  Elige un menú activo en la pestaña <strong>Menús</strong> para gestionar sus categorías.
                </div>
              ) : (
              <DragDropContext onDragEnd={handleReorder}>
                <Droppable droppableId="categories" type="category">
                  {(catDrop) => (
                    <div ref={catDrop.innerRef} {...catDrop.droppableProps} className="overflow-hidden rounded-lg border bg-white" style={{ borderColor: "#E6E4DD" }} data-testid="categories-list">
                      {categories.length === 0 && (
                        <div className="p-8 text-center text-sm" style={{ color: "#6B6A65" }}>Este menú no tiene categorías. Crea una nueva o vincula existentes.</div>
                      )}
                      {categories.map((category, catIndex) => {
                        const subs = subcategoriesByCategory[category.id] || [];
                        const isOpen = !!expandedCats[category.id];
                        return (
                          <Draggable key={category.id} draggableId={category.id} index={catIndex}>
                            {(catDrag, snapshot) => (
                              <div
                                ref={catDrag.innerRef}
                                {...catDrag.draggableProps}
                                className={`border-b last:border-0 ${snapshot.isDragging ? "dragging-item" : ""}`}
                                style={{ borderColor: "#E6E4DD", background: "white", ...catDrag.draggableProps.style }}
                                data-testid={`category-card-${category.id}`}
                              >
                                <div className="flex items-center justify-between gap-3 px-4 py-3" style={{ background: "#FDFBF7" }}>
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span {...catDrag.dragHandleProps} className="drag-handle" data-testid={`drag-category-${category.id}`}>
                                      <GripVertical className="h-4 w-4" style={{ color: "#A89F9A" }} />
                                    </span>
                                    <button onClick={() => toggleCat(category.id)} className="flex items-center" data-testid={`toggle-category-${category.id}`}>
                                      {isOpen ? <ChevronDown className="h-4 w-4" style={{ color: "#6B6A65" }} /> : <ChevronRight className="h-4 w-4" style={{ color: "#6B6A65" }} />}
                                    </button>
                                    <FolderTree className="h-4 w-4 shrink-0" style={{ color: "#C05C46" }} />
                                    {(category.card_image_path || category.card_image_url || category.image_path || category.image_url) && (
                                      <img src={category.card_image_path ? imgUrl(category.card_image_path) : category.card_image_url || (category.image_path ? imgUrl(category.image_path) : category.image_url)} alt="" className="h-7 w-11 shrink-0 rounded border object-cover" style={{ borderColor: "#E6E4DD" }} data-testid={`category-thumb-${category.id}`} />
                                    )}
                                    <span className="font-heading font-semibold text-sm" style={{ color: "#2A2A2A" }}>{category.name}</span>
                                    <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "#C05C4610", color: "#C05C46" }}>{subs.length} subcategorías · {category.product_count} productos</span>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button size="sm" variant="ghost" title="Quitar del menú (desvincular)" onClick={() => unlinkCategory(category.id)} data-testid={`unlink-category-${category.id}`}>
                                      <Link2 className="h-3 w-3" style={{ color: "#A89F9A" }} />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => { setEditingCategory(category); setCategoryCardPreviewError(false); setCategoryMenuPreviewError(false); setCategoryForm({ name: category.name, description: category.description || "", sort_order: category.sort_order ?? "", image_url: category.image_url || "", image_path: category.image_path || "", card_image_url: category.card_image_url || category.image_url || "", card_image_path: category.card_image_path || category.image_path || "", menu_image_url: category.menu_image_url || category.image_url || "", menu_image_path: category.menu_image_path || category.image_path || "" }); setShowCategoryDialog(true); }} data-testid={`edit-category-${category.id}`}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => {
                                      if (!window.confirm("¿Eliminar categoría? También se eliminarán sus subcategorías y productos.")) return;
                                      try {
                                        await api.admin.deleteCategory(category.id);
                                        toast.success("Categoría eliminada");
                                        await refreshEverything();
                                      } catch (error) {
                                        toast.error(formatError(error, "No se pudo eliminar la categoría"));
                                      }
                                    }} data-testid={`delete-category-${category.id}`}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>

                                {isOpen && (
                                  <Droppable droppableId={`subs-${category.id}`} type="subcategory">
                                    {(subDrop) => (
                                      <div ref={subDrop.innerRef} {...subDrop.droppableProps}>
                                        {subs.length === 0 && (
                                          <div className="px-4 py-3 pl-12 text-xs" style={{ color: "#A89F9A" }}>Sin subcategorías</div>
                                        )}
                                        {subs.map((subcategory, subIndex) => (
                                          <Draggable key={subcategory.id} draggableId={subcategory.id} index={subIndex}>
                                            {(subDrag, subSnap) => (
                                              <div
                                                ref={subDrag.innerRef}
                                                {...subDrag.draggableProps}
                                                className={`border-t ${subSnap.isDragging ? "dragging-item" : ""}`}
                                                style={{ borderColor: "#F0EEE8", background: "white", ...subDrag.draggableProps.style }}
                                                data-testid={`subcategory-row-${subcategory.id}`}
                                              >
                                                <div className="flex items-center justify-between gap-3 px-4 py-2.5 pl-8">
                                                  <div className="flex min-w-0 items-center gap-2">
                                                    <span {...subDrag.dragHandleProps} className="drag-handle" data-testid={`drag-subcategory-${subcategory.id}`}>
                                                      <GripVertical className="h-4 w-4" style={{ color: "#C7C0B8" }} />
                                                    </span>
                                                    <button onClick={() => toggleSubcategory(subcategory.id)} className="flex items-center" data-testid={`toggle-subcategory-${subcategory.id}`}>
                                                      {expandedSubs[subcategory.id] ? <ChevronDown className="h-4 w-4" style={{ color: "#6B6A65" }} /> : <ChevronRight className="h-4 w-4" style={{ color: "#6B6A65" }} />}
                                                    </button>
                                                    <span className="text-sm" style={{ color: "#2A2A2A" }}>{subcategory.name}</span>
                                                    <span className="text-[11px]" style={{ color: "#A89F9A" }}>{(productsBySubcategory[subcategory.id] || []).length} productos</span>
                                                  </div>
                                                  <div className="flex gap-1">
                                                    <Button size="sm" variant="ghost" title="Agregar producto" onClick={() => { setEditingProduct(null); setProductForm({ ...initialProductForm, category_id: category.id, subcategory_id: subcategory.id }); setShowProductDialog(true); }} data-testid={`add-product-to-subcategory-${subcategory.id}`}>
                                                      <Plus className="h-3 w-3" style={{ color: "#C05C46" }} />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => { setEditingSubcategory(subcategory); setSubcategoryForm({ category_id: subcategory.category_id, name: subcategory.name, description: subcategory.description || "", sort_order: subcategory.sort_order ?? "" }); setShowSubcategoryDialog(true); }} data-testid={`edit-subcategory-${subcategory.id}`}>
                                                      <Pencil className="h-3 w-3" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => {
                                                      if (!window.confirm("¿Eliminar subcategoría?")) return;
                                                      try {
                                                        await api.admin.deleteSubcategory(subcategory.id);
                                                        toast.success("Subcategoría eliminada");
                                                        await loadCore();
                                                      } catch (error) {
                                                        toast.error(formatError(error, "No se pudo eliminar la subcategoría"));
                                                      }
                                                    }} data-testid={`delete-subcategory-${subcategory.id}`}>
                                                      <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                  </div>
                                                </div>

                                                {expandedSubs[subcategory.id] && (
                                                  <Droppable droppableId={`products-${subcategory.id}`} type="product">
                                                    {(productDrop) => (
                                                      <div ref={productDrop.innerRef} {...productDrop.droppableProps} className="border-t" style={{ borderColor: "#F0EEE8", background: "#FDFBF7" }} data-testid={`products-for-subcategory-${subcategory.id}`}>
                                                        {(productsBySubcategory[subcategory.id] || []).length === 0 && (
                                                          <div className="px-4 py-3 pl-16 text-xs" style={{ color: "#A89F9A" }}>Sin productos</div>
                                                        )}
                                                        {(productsBySubcategory[subcategory.id] || []).map((product, productIndex) => (
                                                          <Draggable key={product.id} draggableId={product.id} index={productIndex}>
                                                            {(productDrag, productSnap) => (
                                                              <div ref={productDrag.innerRef} {...productDrag.draggableProps} className={`flex items-center justify-between gap-3 border-t px-4 py-2 pl-16 ${productSnap.isDragging ? "dragging-item" : ""}`} style={{ borderColor: "#F0EEE8", background: "white", ...productDrag.draggableProps.style }} data-testid={`subcategory-product-row-${product.id}`}>
                                                                <div className="flex min-w-0 items-center gap-2">
                                                                  <span {...productDrag.dragHandleProps} className="drag-handle" data-testid={`drag-product-${product.id}`}><GripVertical className="h-4 w-4" style={{ color: "#C7C0B8" }} /></span>
                                                                  <Package className="h-3.5 w-3.5 shrink-0" style={{ color: "#C05C46" }} />
                                                                  <span className="truncate text-sm" style={{ color: "#2A2A2A" }}>{product.name}</span>
                                                                  <span className="hidden text-xs sm:inline" style={{ color: "#A89F9A" }}>{product.product_units || ""}</span>
                                                                </div>
                                                                <div className="flex shrink-0 items-center gap-1">
                                                                  <span className="mr-1 text-xs font-semibold" style={{ color: "#C05C46" }}>{fmtPrice(product.base_price)}</span>
                                                                  <Button size="sm" variant="ghost" onClick={() => { setEditingProduct(product); setProductForm({ unique_code: product.unique_code, name: product.name, description: product.description || "", category_id: product.category_id, subcategory_id: product.subcategory_id, subcategory_type: product.subcategory_type || "", product_units: product.product_units || "", base_price: product.base_price ?? "", allergens: (product.allergens || []).join(", "), badges: (product.badges || []).join(", "), image_url: product.image_url || "", image_path: product.image_path || "", is_active: product.is_active !== false }); setShowProductDialog(true); }} data-testid={`edit-product-in-subcategory-${product.id}`}><Pencil className="h-3 w-3" /></Button>
                                                                  <Button size="sm" variant="ghost" className="text-red-500" title="Eliminar producto" onClick={async () => {
                                                                    if (!window.confirm("¿Eliminar producto?")) return;
                                                                    try {
                                                                      await api.admin.deleteProduct(product.id);
                                                                      toast.success("Producto eliminado");
                                                                      await refreshEverything();
                                                                    } catch (error) {
                                                                      toast.error(formatError(error, "No se pudo eliminar el producto"));
                                                                    }
                                                                  }} data-testid={`delete-product-in-subcategory-${product.id}`}><Trash2 className="h-3 w-3" /></Button>
                                                                </div>
                                                              </div>
                                                            )}
                                                          </Draggable>
                                                        ))}
                                                        {productDrop.placeholder}
                                                      </div>
                                                    )}
                                                  </Droppable>
                                                )}
                                              </div>
                                            )}
                                          </Draggable>
                                        ))}
                                        {subDrop.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {catDrop.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              )}
            </div>
          )}

          {tab === "catalog" && (
            <div className="animate-fade-in" data-testid="catalog-tab">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-2xl font-heading font-bold" style={{ color: "#2A2A2A" }}>Productos</h2>
                <div className="flex flex-wrap gap-2">
                  <button onClick={downloadProductTemplate} className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium hover:bg-gray-50" style={{ borderColor: "#E6E4DD" }} data-testid="product-template-btn">
                    <Download className="h-3 w-3" style={{ color: "#6B6A65" }} /> Template Productos
                  </button>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium hover:bg-gray-50" style={{ borderColor: "#E6E4DD" }} data-testid="product-import-label">
                    <ImagePlus className="h-3 w-3" style={{ color: "#C05C46" }} /> Importar Productos
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleProductImportFile} data-testid="product-import-input" />
                  </label>
                  <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={() => { setEditingProduct(null); setProductForm(initialProductForm); setShowProductDialog(true); }} data-testid="add-product-btn">
                    <Plus className="mr-2 h-4 w-4" /> Nuevo Producto
                  </Button>
                </div>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-3 h-4 w-4" style={{ color: "#A89F9A" }} />
                <input type="text" placeholder="Buscar productos..." value={catalogSearch} onChange={(event) => setCatalogSearch(event.target.value)} className="w-full rounded-lg border py-2.5 pl-10 pr-4 text-sm" style={{ borderColor: "#E6E4DD" }} data-testid="catalog-search-input" />
              </div>

              <div className="overflow-hidden rounded-lg border bg-white" style={{ borderColor: "#E6E4DD" }} data-testid="products-table-wrapper">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b" style={{ borderColor: "#E6E4DD", background: "#FDFBF7" }}>
                        <th className="p-3 text-left text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Código</th>
                        <th className="p-3 text-left text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Producto</th>
                        <th className="p-3 text-left text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Categoría</th>
                        <th className="p-3 text-left text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Subcategoría</th>
                        <th className="p-3 text-left text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Unidades</th>
                        <th className="p-3 text-right text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Precio Base</th>
                        <th className="p-3 text-right text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.id} className="border-b last:border-0" style={{ borderColor: "#E6E4DD" }} data-testid={`product-row-${product.id}`}>
                          <td className="p-3 text-xs font-mono" style={{ color: "#6B6A65" }}>{product.unique_code}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 shrink-0" style={{ color: "#C05C46" }} />
                              <div>
                                <div className="text-sm font-medium" style={{ color: "#2A2A2A" }}>{product.name}</div>
                                {product.description && <div className="max-w-[280px] truncate text-xs" style={{ color: "#6B6A65" }}>{product.description}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="p-3 text-xs" style={{ color: "#6B6A65" }}>{product.category_name || "-"}</td>
                          <td className="p-3 text-xs" style={{ color: "#6B6A65" }}>{product.subcategory_name || "-"}</td>
                          <td className="p-3 text-xs" style={{ color: "#6B6A65" }}>{product.product_units || "-"}</td>
                          <td className="p-3 text-right text-sm font-bold" style={{ color: "#C05C46" }}>{fmtPrice(product.base_price)}</td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => {
                                setEditingProduct(product);
                                setProductForm({
                                  unique_code: product.unique_code,
                                  name: product.name,
                                  description: product.description || "",
                                  category_id: product.category_id,
                                  subcategory_id: product.subcategory_id,
                                  subcategory_type: product.subcategory_type || "",
                                  product_units: product.product_units || "",
                                  base_price: product.base_price,
                                  allergens: (product.allergens || []).join(", "),
                                  badges: (product.badges || []).join(", "),
                                  image_url: product.image_url || "",
                                  image_path: product.image_path || "",
                                  is_active: product.is_active !== false,
                                });
                                setShowProductDialog(true);
                              }} data-testid={`edit-product-${product.id}`}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => {
                                if (!window.confirm("¿Eliminar producto?")) return;
                                try {
                                  await api.admin.deleteProduct(product.id);
                                  toast.success("Producto eliminado");
                                  await refreshEverything();
                                } catch (error) {
                                  toast.error(formatError(error, "No se pudo eliminar el producto"));
                                }
                              }} data-testid={`delete-product-${product.id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredProducts.length === 0 && (
                        <tr><td colSpan={7} className="p-8 text-center text-sm" style={{ color: "#6B6A65" }}>No hay productos. Crea tu primer producto.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === "users" && (
            <div className="space-y-6" data-testid="users-tab">
              <div className="flex flex-col gap-4 rounded-xl border border-[#E6E4DD] bg-white p-6  lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h2 className="text-2xl font-heading font-bold" style={{ color: "#2A2A2A" }}>Usuarios</h2>
                  <p className="mt-2 text-sm leading-7 text-[#6B6A65]">Gestiona los usuarios administradores con acceso al panel.</p>
                </div>
                <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row">
                  <div className="relative lg:w-[320px]">
                    <Search className="absolute left-3 top-3.5 h-4 w-4 text-[#A89F9A]" />
                    <Input value={usersSearch} onChange={(event) => { setUsersSearch(event.target.value); setUsersPage(1); }} placeholder="Buscar usuario" className="pl-10 border-[#E6E4DD]" data-testid="users-search-input" />
                  </div>
                  <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={() => { setUserForm(initialUserForm); setShowUserDialog(true); }} data-testid="add-user-btn">
                    <Plus className="mr-2 h-4 w-4" /> Nuevo usuario
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-[#E6E4DD] bg-white ">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-[#F8F7F3] text-xs uppercase tracking-[0.18em] text-[#A89F9A]">
                    <tr><th className="px-5 py-4">Nombre</th><th className="px-5 py-4">Email</th><th className="px-5 py-4">Estado</th><th className="px-5 py-4">Acciones</th></tr>
                  </thead>
                  <tbody>
                    {users.map((row) => (
                      <tr key={row.id} className="border-t border-[#E6E4DD]" data-testid={`user-row-${row.id}`}>
                        <td className="px-5 py-4 font-medium">{row.name}</td>
                        <td className="px-5 py-4">{row.email}</td>
                        <td className="px-5 py-4">
                          {row.is_active !== false ? <span className="rounded-full bg-[#e7f3ea] px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#1d6a37]">Activo</span> : <span className="rounded-full bg-[#f6e4e9] px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#7d2138]">Inactivo</span>}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex gap-2">
                            <Button variant="outline" className="border-[#E6E4DD] bg-white" onClick={async () => {
                              try {
                                await api.admin.toggleUserActive(row.id);
                                toast.success("Estado actualizado");
                                await loadUsers();
                              } catch (error) {
                                toast.error(formatError(error, "No se pudo actualizar el estado"));
                              }
                            }} data-testid={`toggle-user-${row.id}`}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" className="border-[#E6E4DD] bg-white text-[#8d263d]" onClick={async () => {
                              if (!window.confirm("¿Eliminar administrador?")) return;
                              try {
                                await api.admin.deleteUser(row.id);
                                toast.success("Administrador eliminado");
                                await loadUsers();
                              } catch (error) {
                                toast.error(formatError(error, "No se pudo eliminar"));
                              }
                            }} data-testid={`delete-user-${row.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between border-t border-[#E6E4DD] px-5 py-4 text-sm text-[#6B6A65]">
                  <span>Página {usersPage} de {usersPages}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" className="border-[#E6E4DD] bg-white" disabled={usersPage <= 1} onClick={() => setUsersPage((prev) => prev - 1)} data-testid="users-prev-page">Anterior</Button>
                    <Button variant="outline" className="border-[#E6E4DD] bg-white" disabled={usersPage >= usersPages} onClick={() => setUsersPage((prev) => prev + 1)} data-testid="users-next-page">Siguiente</Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "smtp" && (
            <div className="space-y-6" data-testid="smtp-tab">
              <div className="rounded-xl border border-[#E6E4DD] bg-white p-6 ">
                <h2 className="text-3xl font-heading">Configuración SMTP</h2>
                <p className="mt-2 text-sm leading-7 text-[#6B6A65]">El panel administrativo usa Magic Link por correo. Aquí defines el servidor SMTP para el envío real del código.</p>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <div className="space-y-2"><Label>Host</Label><Input value={smtpForm.host} onChange={(event) => setSmtpForm((prev) => ({ ...prev, host: event.target.value }))} className="border-[#E6E4DD]" data-testid="smtp-host-input" /></div>
                  <div className="space-y-2"><Label>Puerto</Label><Input type="number" value={smtpForm.port} onChange={(event) => setSmtpForm((prev) => ({ ...prev, port: Number(event.target.value) }))} className="border-[#E6E4DD]" data-testid="smtp-port-input" /></div>
                  <div className="space-y-2"><Label>Usuario</Label><Input value={smtpForm.username} onChange={(event) => setSmtpForm((prev) => ({ ...prev, username: event.target.value }))} className="border-[#E6E4DD]" data-testid="smtp-username-input" /></div>
                  <div className="space-y-2"><Label>Contraseña</Label><Input type="password" value={smtpForm.password} onChange={(event) => setSmtpForm((prev) => ({ ...prev, password: event.target.value }))} className="border-[#E6E4DD]" data-testid="smtp-password-input" /></div>
                  <div className="space-y-2"><Label>Seguridad</Label><select value={smtpForm.security} onChange={(event) => setSmtpForm((prev) => ({ ...prev, security: event.target.value }))} className="h-11 w-full rounded-xl border border-[#E6E4DD] bg-white px-3 text-sm" data-testid="smtp-security-select"><option value="TLS">TLS</option><option value="STARTTLS">STARTTLS</option><option value="SSL">SSL</option></select></div>
                  <div className="space-y-2"><Label>Email remitente</Label><Input value={smtpForm.sender_email} onChange={(event) => setSmtpForm((prev) => ({ ...prev, sender_email: event.target.value }))} className="border-[#E6E4DD]" data-testid="smtp-sender-email-input" /></div>
                  <div className="space-y-2 md:col-span-2"><Label>Nombre remitente</Label><Input value={smtpForm.sender_name} onChange={(event) => setSmtpForm((prev) => ({ ...prev, sender_name: event.target.value }))} className="border-[#E6E4DD]" data-testid="smtp-sender-name-input" /></div>
                </div>

                <div className="mt-6 flex items-center gap-3 rounded-2xl bg-[#F8F7F3] px-4 py-4">
                  <Switch checked={smtpForm.enabled} onCheckedChange={(checked) => setSmtpForm((prev) => ({ ...prev, enabled: checked }))} data-testid="smtp-enabled-switch" />
                  <div>
                    <p className="font-medium">SMTP habilitado</p>
                    <p className="text-sm text-[#6B6A65]">Si está activo, el código se enviará por correo real.</p>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={async () => {
                    try {
                      await api.admin.updateSmtp(smtpForm);
                      toast.success("Configuración SMTP guardada");
                    } catch (error) {
                      toast.error(formatError(error, "No se pudo guardar SMTP"));
                    }
                  }} data-testid="save-smtp-btn">Guardar SMTP</Button>
                  <Button variant="outline" className="border-[#E6E4DD] bg-white" disabled={smtpTesting} onClick={async () => {
                    setSmtpTesting(true);
                    try {
                      const { data } = await api.admin.testSmtp();
                      toast.success(data.message);
                    } catch (error) {
                      toast.error(formatError(error, "No se pudo probar SMTP"));
                    } finally {
                      setSmtpTesting(false);
                    }
                  }} data-testid="test-smtp-btn">{smtpTesting ? "Probando..." : "Probar conexión"}</Button>
                </div>
              </div>
            </div>
          )}
        </main>

      <Dialog open={showMenuImportDialog} onOpenChange={setShowMenuImportDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="menu-import-dialog">
          <DialogHeader><DialogTitle className="font-heading">Importar menús</DialogTitle></DialogHeader>
          {menuImportPreview && (
            <div className="space-y-4">
              <div className="rounded-lg p-4 text-sm" style={{ background: "#FDFBF7", color: "#6B6A65" }} data-testid="menu-import-summary">
                {menuImportPreview.menus.length} menús · {menuImportPreview.items.length} filas de estructura · {menuImportPreview.errors.length} errores
              </div>
              <div className="max-h-[340px] overflow-auto rounded-lg border" style={{ borderColor: "#E6E4DD" }}>
                <table className="w-full text-left text-sm" data-testid="menu-import-preview-table">
                  <thead><tr className="border-b" style={{ borderColor: "#E6E4DD", background: "#FDFBF7" }}>
                    <th className="p-3 text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Menú</th>
                    <th className="p-3 text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Slug</th>
                    <th className="p-3 text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Acción</th>
                  </tr></thead>
                  <tbody>
                    {menuImportPreview.menus.map((menu) => (
                      <tr key={`menu-import-${menu.row}`} className="border-b last:border-0" style={{ borderColor: "#E6E4DD" }}>
                        <td className="p-3 text-sm">{menu.name}</td>
                        <td className="p-3 text-sm">{menu.slug}</td>
                        <td className="p-3 text-xs font-medium">{menu.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {menuImportPreview.errors.length > 0 && (
                <div className="rounded-lg p-4 text-sm" style={{ background: "#F7E6EA", color: "#7A2038" }} data-testid="menu-import-errors">
                  {menuImportPreview.errors.map((error) => <div key={`menu-error-${error.row}`}>Fila {error.row}: {error.error}</div>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" disabled={menuImporting || !menuImportPreview || menuImportPreview.menus.length === 0} onClick={applyMenuImportNow} data-testid="apply-menu-import-btn">
              {menuImporting ? "Aplicando..." : "Aplicar estructura de menús"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showProductImportDialog} onOpenChange={setShowProductImportDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="product-import-dialog">
          <DialogHeader><DialogTitle className="font-heading">Importar productos</DialogTitle></DialogHeader>
          {productImportPreview && (
            <div className="space-y-4">
              <div className="rounded-lg p-4 text-sm" style={{ background: "#FDFBF7", color: "#6B6A65" }}>
                {productImportPreview.items.length} filas válidas · {productImportPreview.errors.length} errores
              </div>
              <div className="max-h-[340px] overflow-auto rounded-lg border" style={{ borderColor: "#E6E4DD" }}>
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b" style={{ borderColor: "#E6E4DD", background: "#FDFBF7" }}>
                    <th className="p-3 text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Acción</th>
                    <th className="p-3 text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Producto</th>
                    <th className="p-3 text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Precio</th>
                    <th className="p-3 text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Categoría</th>
                    <th className="p-3 text-xs font-body font-semibold uppercase" style={{ color: "#6B6A65" }}>Subcategoría</th>
                  </tr></thead>
                  <tbody>
                    {productImportPreview.items.map((item) => (
                      <tr key={`product-import-${item.row}`} className="border-b last:border-0" style={{ borderColor: "#E6E4DD" }}>
                        <td className="p-3 text-xs font-medium">{item.action}</td>
                        <td className="p-3 text-sm">{item.name}</td>
                        <td className="p-3 text-sm">{item.base_price}</td>
                        <td className="p-3 text-sm">{item.category_name}</td>
                        <td className="p-3 text-sm">{item.subcategory_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {productImportPreview.errors.length > 0 && (
                <div className="rounded-lg p-4 text-sm" style={{ background: "#F7E6EA", color: "#7A2038" }} data-testid="product-import-errors">
                  {productImportPreview.errors.map((error) => <div key={`product-error-${error.row}`}>Fila {error.row}: {error.error}</div>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" disabled={productImporting || !productImportPreview || productImportPreview.items.length === 0} onClick={applyProductImportNow} data-testid="apply-products-import-btn">
              {productImporting ? "Aplicando..." : "Aplicar importación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showLinkCatDialog} onOpenChange={setShowLinkCatDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="link-category-dialog">
          <DialogHeader><DialogTitle className="font-heading">Vincular categoría existente</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: "#6B6A65" }}>Agrega al menú <strong>{activeMenu?.name}</strong> una categoría global existente (incluye sus subcategorías).</p>
          <div className="max-h-[360px] space-y-2 overflow-auto">
            {availableCats.length === 0 && <p className="py-6 text-center text-sm" style={{ color: "#A89F9A" }}>No hay categorías disponibles para vincular.</p>}
            {availableCats.map((category) => (
              <div key={category.id} className="flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5" style={{ borderColor: "#E6E4DD" }} data-testid={`available-category-${category.id}`}>
                <div className="flex items-center gap-2">
                  <FolderTree className="h-4 w-4" style={{ color: "#C05C46" }} />
                  <span className="text-sm font-medium" style={{ color: "#2A2A2A" }}>{category.name}</span>
                  <span className="text-xs" style={{ color: "#A89F9A" }}>({category.subcategory_count} subcat.)</span>
                </div>
                <Button size="sm" className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={() => linkCategory(category.id)} data-testid={`link-category-${category.id}`}>
                  <Plus className="mr-1 h-3 w-3" /> Vincular
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="!flex max-h-[calc(100vh-2rem)] !flex-col sm:max-w-lg" data-testid="category-dialog">
          <DialogHeader><DialogTitle>{editingCategory ? "Editar categoría" : "Nueva categoría"}</DialogTitle></DialogHeader>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2"><Label>Nombre</Label><Input value={categoryForm.name} onChange={(event) => setCategoryForm((prev) => ({ ...prev, name: event.target.value }))} data-testid="category-name-input" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Input value={categoryForm.description} onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))} data-testid="category-description-input" /></div>
            <div className="space-y-2"><Label>Orden</Label><Input value={categoryForm.sort_order} onChange={(event) => setCategoryForm((prev) => ({ ...prev, sort_order: event.target.value }))} data-testid="category-order-input" /></div>
            <div className="space-y-2">
              <Label>Imagen de tarjeta</Label>
              <div className="flex items-center gap-3">
                <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium" style={{ borderColor: "#E6E4DD" }} data-testid="category-card-image-upload-label">
                  <ImagePlus className="h-4 w-4" /> {uploadingImage ? "Subiendo..." : "Importar"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => handleImageUpload(event, (path) => { setCategoryCardPreviewError(false); setCategoryForm((prev) => ({ ...prev, card_image_path: path, card_image_url: "" })); })} data-testid="category-card-image-upload-input" />
                </label>
              </div>
              <Input value={categoryForm.card_image_url} onChange={(event) => { setCategoryCardPreviewError(false); setCategoryForm((prev) => ({ ...prev, card_image_url: event.target.value, card_image_path: "" })); }} placeholder="URL de imagen de tarjeta" data-testid="category-card-image-url-input" />
              {(categoryForm.card_image_path || categoryForm.card_image_url) && (
                <div className="mt-3 overflow-hidden rounded-lg border" style={{ borderColor: "#E6E4DD", aspectRatio: "3 / 1", background: "#F8F7F3" }} data-testid="category-card-image-preview-container">
                  {!categoryCardPreviewError ? <img src={categoryForm.card_image_path ? imgUrl(categoryForm.card_image_path) : categoryForm.card_image_url} alt="Vista previa de imagen de tarjeta" className="h-full w-full object-cover" onError={() => setCategoryCardPreviewError(true)} data-testid="category-card-image-preview" /> : <div className="flex h-full items-center justify-center text-xs" style={{ color: "#A89F9A" }} data-testid="category-card-image-preview-error">No se pudo cargar esta imagen</div>}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Imagen de carta</Label>
              <div className="flex items-center gap-3">
                <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium" style={{ borderColor: "#E6E4DD" }} data-testid="category-menu-image-upload-label">
                  <ImagePlus className="h-4 w-4" /> {uploadingImage ? "Subiendo..." : "Importar"}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(event) => handleImageUpload(event, (path) => { setCategoryMenuPreviewError(false); setCategoryForm((prev) => ({ ...prev, menu_image_path: path, menu_image_url: "" })); })} data-testid="category-menu-image-upload-input" />
                </label>
              </div>
              <Input value={categoryForm.menu_image_url} onChange={(event) => { setCategoryMenuPreviewError(false); setCategoryForm((prev) => ({ ...prev, menu_image_url: event.target.value, menu_image_path: "" })); }} placeholder="URL de imagen de carta" data-testid="category-menu-image-url-input" />
              {(categoryForm.menu_image_path || categoryForm.menu_image_url) && (
                <div className="mt-3 overflow-hidden rounded-lg border" style={{ borderColor: "#E6E4DD", aspectRatio: "5 / 2", background: "#F8F7F3" }} data-testid="category-menu-image-preview-container">
                  {!categoryMenuPreviewError ? <img src={categoryForm.menu_image_path ? imgUrl(categoryForm.menu_image_path) : categoryForm.menu_image_url} alt="Vista previa de imagen de carta" className="h-full w-full object-cover" onError={() => setCategoryMenuPreviewError(true)} data-testid="category-menu-image-preview" /> : <div className="flex h-full items-center justify-center text-xs" style={{ color: "#A89F9A" }} data-testid="category-menu-image-preview-error">No se pudo cargar esta imagen</div>}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t pt-4" style={{ borderColor: "#E6E4DD" }}>
            <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={async () => {
              try {
                const payload = { ...categoryForm, sort_order: categoryForm.sort_order === "" ? null : Number(categoryForm.sort_order) };
                if (editingCategory) await api.admin.updateCategory(editingCategory.id, payload);
                else await api.admin.createCategory({ ...payload, menu_id: selectedMenuId });
                toast.success(editingCategory ? "Categoría actualizada" : "Categoría creada");
                setShowCategoryDialog(false);
                await refreshEverything();
              } catch (error) {
                toast.error(formatError(error, "No se pudo guardar la categoría"));
              }
            }} data-testid="save-category-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSubcategoryDialog} onOpenChange={setShowSubcategoryDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="subcategory-dialog">
          <DialogHeader><DialogTitle>{editingSubcategory ? "Editar subcategoría" : "Nueva subcategoría"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Categoría</Label>
              <select value={subcategoryForm.category_id} onChange={(event) => setSubcategoryForm((prev) => ({ ...prev, category_id: event.target.value }))} className="h-11 w-full rounded-xl border border-[#E6E4DD] bg-white px-3 text-sm" data-testid="subcategory-category-select">
                <option value="">Selecciona una categoría</option>
                {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
            </div>
            <div className="space-y-2"><Label>Nombre</Label><Input value={subcategoryForm.name} onChange={(event) => setSubcategoryForm((prev) => ({ ...prev, name: event.target.value }))} data-testid="subcategory-name-input" /></div>
            <div className="space-y-2"><Label>Descripción</Label><Input value={subcategoryForm.description} onChange={(event) => setSubcategoryForm((prev) => ({ ...prev, description: event.target.value }))} data-testid="subcategory-description-input" /></div>
            <div className="space-y-2"><Label>Orden</Label><Input value={subcategoryForm.sort_order} onChange={(event) => setSubcategoryForm((prev) => ({ ...prev, sort_order: event.target.value }))} data-testid="subcategory-order-input" /></div>
          </div>
          <DialogFooter>
            <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={async () => {
              try {
                const payload = { ...subcategoryForm, sort_order: subcategoryForm.sort_order === "" ? null : Number(subcategoryForm.sort_order) };
                if (editingSubcategory) await api.admin.updateSubcategory(editingSubcategory.id, payload);
                else await api.admin.createSubcategory({ ...payload, menu_id: selectedMenuId });
                toast.success(editingSubcategory ? "Subcategoría actualizada" : "Subcategoría creada");
                setShowSubcategoryDialog(false);
                await refreshEverything();
              } catch (error) {
                toast.error(formatError(error, "No se pudo guardar la subcategoría"));
              }
            }} data-testid="save-subcategory-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="product-dialog">
          <DialogHeader><DialogTitle>{editingProduct ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2"><Label>Código</Label><Input value={productForm.unique_code} onChange={(event) => setProductForm((prev) => ({ ...prev, unique_code: event.target.value.toUpperCase() }))} data-testid="product-code-input" /></div>
            <div className="space-y-2"><Label>Nombre</Label><Input value={productForm.name} onChange={(event) => setProductForm((prev) => ({ ...prev, name: event.target.value }))} data-testid="product-name-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Descripción</Label><Input value={productForm.description} onChange={(event) => setProductForm((prev) => ({ ...prev, description: event.target.value }))} data-testid="product-description-input" /></div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <select value={productForm.category_id} onChange={(event) => setProductForm((prev) => ({ ...prev, category_id: event.target.value, subcategory_id: "" }))} className="h-11 w-full rounded-xl border border-[#E6E4DD] bg-white px-3 text-sm" data-testid="product-category-select">
                <option value="">Selecciona una categoría</option>
                {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Subcategoría</Label>
              <select value={productForm.subcategory_id} onChange={(event) => setProductForm((prev) => ({ ...prev, subcategory_id: event.target.value }))} className="h-11 w-full rounded-xl border border-[#E6E4DD] bg-white px-3 text-sm" data-testid="product-subcategory-select">
                <option value="">Selecciona una subcategoría</option>
                {productDialogSubcategories.map((subcategory) => <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>)}
              </select>
            </div>
            <div className="space-y-2"><Label>Tipo de subcategoría</Label><Input value={productForm.subcategory_type} onChange={(event) => setProductForm((prev) => ({ ...prev, subcategory_type: event.target.value }))} data-testid="product-subcategory-type-input" /></div>
            <div className="space-y-2"><Label>Unidades</Label><Input value={productForm.product_units} onChange={(event) => setProductForm((prev) => ({ ...prev, product_units: event.target.value }))} data-testid="product-units-input" /></div>
            <div className="space-y-2"><Label>Precio base</Label><Input type="text" value={productForm.base_price} onChange={(event) => setProductForm((prev) => ({ ...prev, base_price: event.target.value }))} data-testid="product-base-price-input" /></div>
            <div className="space-y-2"><Label>Badges (coma)</Label><Input value={productForm.badges} onChange={(event) => setProductForm((prev) => ({ ...prev, badges: event.target.value }))} data-testid="product-badges-input" /></div>
            <div className="space-y-2"><Label>Alérgenos (coma)</Label><Input value={productForm.allergens} onChange={(event) => setProductForm((prev) => ({ ...prev, allergens: event.target.value }))} data-testid="product-allergens-input" /></div>
            <div className="space-y-2"><Label>URL imagen</Label><Input value={productForm.image_url} onChange={(event) => setProductForm((prev) => ({ ...prev, image_url: event.target.value }))} data-testid="product-image-url-input" /></div>
            <div className="space-y-2">
              <Label>Imagen subida</Label>
              <div className="flex gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-[#E6E4DD] bg-[#F8F7F3] px-4 py-2 text-sm font-medium" data-testid="product-image-upload-label">
                  <ImagePlus className="h-4 w-4" /> {uploadingImage ? "Subiendo..." : "Subir"}
                  <input type="file" accept="image/*" className="hidden" onChange={(event) => handleImageUpload(event, (path) => setProductForm((prev) => ({ ...prev, image_path: path })))} data-testid="product-image-upload-input" />
                </label>
                {productForm.image_path && <div className="rounded-xl bg-[#F8F7F3] px-3 py-2 text-xs text-[#6B6A65]">Archivo listo</div>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={async () => {
              try {
                const payload = {
                  ...productForm,
                  base_price: String(productForm.base_price ?? ""),
                  allergens: productForm.allergens ? productForm.allergens.split(",").map((item) => item.trim()).filter(Boolean) : [],
                  badges: productForm.badges ? productForm.badges.split(",").map((item) => item.trim()).filter(Boolean) : [],
                };
                if (editingProduct) await api.admin.updateProduct(editingProduct.id, payload);
                else await api.admin.createProduct(payload);
                toast.success(editingProduct ? "Producto actualizado" : "Producto creado");
                setShowProductDialog(false);
                await refreshEverything();
              } catch (error) {
                toast.error(formatError(error, "No se pudo guardar el producto"));
              }
            }} data-testid="save-product-btn">Guardar producto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMenuDialog} onOpenChange={setShowMenuDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid="menu-dialog">
          <DialogHeader><DialogTitle>{editingMenu ? "Editar menú" : "Nuevo menú"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2"><Label>Nombre</Label><Input value={menuForm.name} onChange={(event) => setMenuForm((prev) => ({ ...prev, name: event.target.value }))} data-testid="menu-name-input" /></div>
            <div className="space-y-2"><Label>Slug público</Label><Input value={menuForm.slug} onChange={(event) => setMenuForm((prev) => ({ ...prev, slug: event.target.value }))} data-testid="menu-slug-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Descripción</Label><Input value={menuForm.description} onChange={(event) => setMenuForm((prev) => ({ ...prev, description: event.target.value }))} data-testid="menu-description-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Plantilla</Label><Input value="Gourmet" disabled data-testid="menu-template-input" /></div>
            <div className="flex items-center gap-3 rounded-2xl bg-[#F8F7F3] px-4 py-4"><Switch checked={menuForm.is_active} onCheckedChange={(checked) => setMenuForm((prev) => ({ ...prev, is_active: checked }))} data-testid="menu-active-switch" /><div><p className="font-medium">Publicado</p><p className="text-sm text-[#6B6A65]">El menú queda disponible en el enlace público.</p></div></div>
            <div className="flex items-center gap-3 rounded-2xl bg-[#F8F7F3] px-4 py-4"><Switch checked={menuForm.seo_index} onCheckedChange={(checked) => setMenuForm((prev) => ({ ...prev, seo_index: checked }))} data-testid="menu-seo-switch" /><div><p className="font-medium">Permitir indexación</p><p className="text-sm text-[#6B6A65]">Control del meta robots en la vista pública.</p></div></div>
          </div>
          <DialogFooter>
            <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={async () => {
              try {
                const payload = { ...menuForm, template_slug: "gourmet" };
                if (editingMenu) await api.admin.updateMenu(editingMenu.id, payload);
                else await api.admin.createMenu(payload);
                toast.success(editingMenu ? "Menú actualizado" : "Menú creado");
                setShowMenuDialog(false);
                await refreshEverything();
              } catch (error) {
                toast.error(formatError(error, "No se pudo guardar el menú"));
              }
            }} data-testid="save-menu-btn">Guardar menú</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="user-dialog">
          <DialogHeader><DialogTitle>Nuevo administrador</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nombre</Label><Input value={userForm.name} onChange={(event) => setUserForm((prev) => ({ ...prev, name: event.target.value }))} data-testid="user-name-input" /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={userForm.email} onChange={(event) => setUserForm((prev) => ({ ...prev, email: event.target.value }))} data-testid="user-email-input" /></div>
          </div>
          <DialogFooter>
            <Button className="bg-[#C05C46] hover:bg-[#a94f3c] text-white" onClick={async () => {
              try {
                await api.admin.createUser(userForm);
                toast.success("Administrador creado");
                setShowUserDialog(false);
                await loadUsers();
                await loadCore();
              } catch (error) {
                toast.error(formatError(error, "No se pudo crear el administrador"));
              }
            }} data-testid="save-user-btn">Crear administrador</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}